import Badge from 'components/base/Badge';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useBranchContext } from 'providers/BranchProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { formatLongIsoDateOrDash } from 'utils/formatLongIsoDate';
import { TCustomerResupplyRequest } from 'utils/types';
import { getUnitLabel } from 'utils/unitOptions';
import DataTable from '../BranchProductInventory/DataTable';

const FULFILLMENT_OPTIONS = [
  { value: 'Pending', label: 'Still to fulfill' },
  { value: 'Notified', label: 'Already notified' },
  { value: 'All', label: 'All requests' }
] as const;

const fulfillmentBadge = (status: string) => {
  switch (status) {
    case 'AwaitingStock':
      return (
        <Badge bg="info" className="fs-10">
          On purchase order
        </Badge>
      );
    case 'Notified':
      return (
        <Badge bg="success" className="fs-10">
          Customer notified
        </Badge>
      );
    default:
      return (
        <Badge bg="warning" className="fs-10">
          Awaiting order
        </Badge>
      );
  }
};

const CustomerRequests = () => {
  const { selectedBranch } = useBranchContext();
  const [fulfillment, setFulfillment] = useState<string>('Pending');

  const columns: ColumnDef<TCustomerResupplyRequest>[] = useMemo(
    () => [
      {
        id: 'customerName',
        accessorKey: 'customerName',
        header: 'Customer',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <div>
            <div className="fw-semibold">{original.customerName || 'Customer'}</div>
            <div className="text-body-secondary fs-10">
              {original.customerContactNumber || '—'}
            </div>
          </div>
        )
      },
      {
        id: 'productName',
        accessorKey: 'productName',
        header: 'Item',
        enableSorting: true,
        cell: ({ row: { original } }) => {
          const name = original.productName || 'Unnamed product';
          return original.productId ? (
            <Link
              to={`/product/${encodeURIComponent(
                selectedBranch?.branchDomain as string
              )}/${original.productId}`}
              className="text-decoration-none fw-bold"
            >
              {name}
            </Link>
          ) : (
            <span className="fw-bold">{name}</span>
          );
        }
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: 'Qty',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <span>
            {original.quantity} {getUnitLabel(original.unitOfMeasure)}
          </span>
        )
      },
      {
        id: 'quantityOnHand',
        accessorKey: 'quantityOnHand',
        header: 'On hand',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.quantityOnHand == null ? (
            <span className="text-body-tertiary">—</span>
          ) : (
            <span>{original.quantityOnHand}</span>
          )
      },
      {
        id: 'status',
        accessorKey: 'fulfillmentStatus',
        header: 'Fulfillment',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column gap-1">
            {fulfillmentBadge(original.fulfillmentStatus)}
            {original.orderedOnPurchaseOrderId ? (
              <Link
                to={`/purchase-order-details/${original.orderedOnPurchaseOrderId}`}
                className="fs-10"
              >
                PO #{original.orderedOnPurchaseOrderId}
              </Link>
            ) : original.fulfillmentStatus === 'PendingOrder' ? (
              <Link to="/resupply" className="fs-10">
                Open on resupply
              </Link>
            ) : null}
          </div>
        )
      },
      {
        id: 'createdOn',
        accessorKey: 'createdOn',
        header: 'Requested',
        enableSorting: true,
        cell: ({ row: { original } }) => (
          <span>{formatLongIsoDateOrDash(original.createdOn)}</span>
        )
      },
      {
        id: 'customerNotifiedOn',
        accessorKey: 'customerNotifiedOn',
        header: 'Notified',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <span>{formatLongIsoDateOrDash(original.customerNotifiedOn)}</span>
        )
      },
      {
        id: 'comment',
        accessorKey: 'comment',
        header: 'Note',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.comment ? (
            <span
              className="text-body-secondary fs-9"
              title={original.comment}
              style={{ display: 'inline-block', maxWidth: '16rem', whiteSpace: 'normal' }}
            >
              {original.comment}
            </span>
          ) : (
            <span className="text-body-tertiary">—</span>
          )
      }
    ],
    [selectedBranch?.branchDomain]
  );

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (!selectedBranch?.branchDomain) {
      throw new Error('Branch domain not set');
    }
    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch.branchDomain}/resupply/customer-requests`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc,
          fulfillment
        }
      }
    );
    return data;
  };

  return (
    <>
      <h4 className="mb-3">Customer requests</h4>
      <p className="text-body-secondary fs-9 mb-3">
        Items customers asked to be reminded about when they are back in stock.
        Still-to-fulfill covers requests waiting to be ordered and those already
        on a purchase order. The customer is texted after the procurement
        invoice is approved.
      </p>
      <DataTable
        queryKey={[
          'customer-resupply-requests',
          selectedBranch?.branchDomain,
          fulfillment
        ]}
        queryFn={fetchData}
        columns={columns}
        defaultOrderBy="createdOn"
        defaultOrderByDesc
        filters={{ fulfillment }}
        rightSideActions={
          <Form.Select
            style={{ height: '2.4rem', width: '16rem' }}
            value={fulfillment}
            onChange={e => setFulfillment(e.target.value)}
            aria-label="Filter customer requests by fulfillment"
          >
            {FULFILLMENT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        }
      />
    </>
  );
};

export default CustomerRequests;
